import { useEffect, useRef, useState } from "react"
import { POI } from "@state/mapStore"
import { useStore } from "@state/store"
import { initMap, loadScript } from "@lib/utils/google.maps"
import { styles } from "./mapStyles"
import { useProjectDataStore } from "@state/projectDataStore"
import { useMapStore } from "@state/mapStore"
import OfflineMap from "./components/OfflineMap"
import { poisWithDistances } from "./components/data/poisWithDistances"
import GoogleMap from "./components/GoogleMap"

const Map = () => {
  const mapType: string = import.meta.env.VITE_MAP_TYPE
  const { mapData } = useProjectDataStore((s) => ({
    mapData: s.mapData,
  }))
  const { setMapInstance } = useMapStore((s) => ({
    setMapInstance: s.setMapinstance,
  }))

  const { mapStyling, mapPOIS, mapSettings, categories = [] } = mapData || {}
  const [places, setPlaces] = useState<POI[]>(poisWithDistances)
  const [filteredPOIS, setFilteredPOIS] = useState<POI[]>([])
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [directionsRenderer, setDirectionsRenderer] =
    useState<google.maps.DirectionsRenderer | null>(null)

  const panelRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<HTMLDivElement>(null)

  const syncState = useStore((s) => s.syncState)

  // Ensure categories is an array, if it's not, use an empty array
  // this is to prevent the app from crashing if the categories are not set
  const safeCategories = Array.isArray(categories) ? categories : []
  const categoriesWithAll = [...safeCategories]

  useEffect(() => {
    syncState({
      contentControls: {
        categories: categoriesWithAll,
        index: 0,
      },
    })
    return () => {
      syncState({
        contentControls: null,
      })
    }
  }, [categories])

  useEffect(() => {
    const initializeMap = async () => {
      if (window.google && window.google.maps) {
        const mapInstance = await initMap({
          mapRef: mapRef,
          mainMarker: mapPOIS?.mainMarker,
          zoom: 14,
          center: mapPOIS?.mainMarker.coordinates,
          disableDefaultUI: true,
          mapTypeControl: false,
          mapStyling: mapStyling?.styling,
        })
        mapInstance.setOptions({
          minZoom: 13,
          maxZoom: 18,
        })
        const color = getComputedStyle(document.body).getPropertyValue(
          "--secondary",
        )
        const directionsRendererInstance =
          new window.google.maps.DirectionsRenderer()
        directionsRendererInstance.setMap(mapInstance)
        directionsRendererInstance.setPanel(panelRef.current!)
        directionsRendererInstance.setOptions({
          suppressMarkers: true,
          polylineOptions: {
            strokeColor: color,
            strokeWeight: 4,
            strokeOpacity: 1,
          },
          suppressInfoWindows: true,
        })
        setDirectionsRenderer(directionsRendererInstance)

        setMapInstance(mapInstance)
        setMap(mapInstance)
      }
    }

    loadScript()
      .then(initializeMap)
      .catch((err) => console.error("Error loading Google Maps script:", err))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setFilteredPOIS(places)
    const filterKey = "TRANSIT"
    const filtered = places.filter((place) => {
      const distance = Number(place.distances[filterKey].distance.split(" ")[0])
      return distance
    })
    setFilteredPOIS(filtered)
  }, [places])

  return mapType === "online" ? (
    <GoogleMap
      map={map}
      panelRef={panelRef}
      directionsRenderer={directionsRenderer}
      categories={categoriesWithAll}
      mapSettings={mapSettings}
      mapStyling={{
        style: styles["silver"],
        customColors: {
          landscapeColor: "#f8f3df",
          waterColor: "#e2dcc8",
          parksColor: "#e3deca",
          labelsColor: "#8c8c8c",
          localColor: "#f8f3df",
          arterialColor: "#FFFFFF",
        },
        // style: styles[mapStyling?.styleKey],
        // customColors: mapStyling?.customColors,
      }}
      withDirections={mapSettings?.withDirections}
      pois={filteredPOIS}
      mainMarker={mapPOIS?.mainMarker}
      poiSettings={mapPOIS?.poiSettings}
      mapRef={mapRef}
    />
  ) : (
    <OfflineMap
      pois={filteredPOIS}
      mainMarker={mapPOIS?.mainMarker}
      categories={categoriesWithAll}
      withDirections={mapSettings?.withDirections}
      panelRef={panelRef}
    />
  )
}

export default Map
